/*
 * Страница каталога, ограниченная одним kind/type: «Кластеры» и «Модели» в сайдбаре.
 * Это тот же CatalogTable с фильтрами каталога, но kind зафиксирован скрытым пикером, type —
 * фильтром напрямую, а колонки — предметные (окружение, домен, чемпион, сервинг).
 */
import { ReactNode, useEffect } from 'react';
import {
  Content,
  ContentHeader,
  CreateButton,
  PageWithHeader,
  SupportButton,
  TableColumn,
} from '@backstage/core-components';
import { CatalogTable, CatalogTableRow } from '@backstage/plugin-catalog';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityListProvider,
  EntityOwnerPicker,
  EntityTagPicker,
  EntityTypeFilter,
  UserListPicker,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { SectionId } from '../../theme/sections';

/**
 * Тип страницы — EntityTypeFilter напрямую, не EntityTypePicker: пикер оставляет в фильтре только
 * типы, у которых уже есть сущности, и на пустой странице (кластеров ещё нет) фильтр по типу
 * пропадал — «Кластеры» показывали все Resource, например GitLab платформы.
 */
const EntityTypesFilter = ({ types }: { types: string[] }) => {
  const { updateFilters } = useEntityList();
  const key = types.join(',');
  useEffect(() => {
    updateFilters({ type: new EntityTypeFilter(key.split(',')) });
  }, [updateFilters, key]);
  return null;
};

type CreateLink = { title: string; to: string };

export type TypedCatalogPageProps = {
  /** Раздел меню — тема шапки с водяным знаком раздела (theme/sections). */
  section: SectionId;
  title: string;
  subtitle?: string;
  kind: 'component' | 'resource';
  /** Один тип или несколько (например, kafka-cluster и postgres-cluster на одной странице). */
  type: string | string[];
  columns: TableColumn<CatalogTableRow>[];
  /** Кнопка создания в шапке; несколько — по кнопке на каждый шаблон (Kafka и PostgreSQL);
   * нет — раздел без создания (RustFS ставится вместе с кластером). */
  create?: CreateLink | CreateLink[];
  emptyContent: ReactNode;
  /** Фильтр по lifecycle есть только у Component. */
  withLifecycle?: boolean;
  support?: string;
};

export const TypedCatalogPage = ({
  section,
  title,
  subtitle,
  kind,
  type,
  columns,
  create,
  emptyContent,
  withLifecycle,
  support,
}: TypedCatalogPageProps) => (
  <PageWithHeader title={title} subtitle={subtitle} themeId={section}>
    <Content>
      <ContentHeader title="">
        {(create ? [create].flat() : []).map(c => (
          <CreateButton key={c.to} title={c.title} to={c.to} />
        ))}
        {support && <SupportButton>{support}</SupportButton>}
      </ContentHeader>
      <EntityListProvider>
        <CatalogFilterLayout>
          <CatalogFilterLayout.Filters>
            <EntityKindPicker initialFilter={kind} hidden />
            <EntityTypesFilter types={Array.isArray(type) ? type : [type]} />
            <UserListPicker initialFilter="all" />
            <EntityOwnerPicker />
            {withLifecycle && <EntityLifecyclePicker />}
            <EntityTagPicker />
          </CatalogFilterLayout.Filters>
          <CatalogFilterLayout.Content>
            <CatalogTable
              columns={columns}
              emptyContent={emptyContent}
              tableOptions={{ padding: 'dense' }}
            />
          </CatalogFilterLayout.Content>
        </CatalogFilterLayout>
      </EntityListProvider>
    </Content>
  </PageWithHeader>
);
